/**
 * TrackContext: JitsiLocalTrack / JitsiRemoteTrack 객체를 trackStore로 관리.
 *
 * JitsiTrack은 non-serializable이므로 Redux 밖에서 관리한다.
 * useSyncExternalStore 기반으로 참여자별 세분화된 구독을 지원하여
 * 특정 참여자의 트랙 변경 시 해당 컴포넌트만 리렌더링된다.
 *
 * 업데이트 흐름:
 *   ConferenceConnector 이벤트 → trackStore.setXxx → 해당 구독자만 재렌더
 */
import React from 'react';
import {
  trackStore,
  useLocalVideoTrack as _useLocalVideoTrack,
  useLocalAudioTrack as _useLocalAudioTrack,
  useLocalScreenShareTrack as _useLocalScreenShareTrack,
  useRemoteTracks as _useRemoteTracks,
  useAllRemoteTracks as _useAllRemoteTracks,
} from '@/stores/trackStore';
import type { RemoteTrackSet, TrackState } from '@/stores/trackStore';
import type { JitsiLocalTrack, JitsiRemoteTrack } from '@/types/jitsi';

// ─── 타입 re-export ──────────────────────────────────────────────────────────
export type { RemoteTrackSet, TrackState };

export interface TrackActions {
  setLocalVideoTrack: (track: JitsiLocalTrack | null) => void;
  setLocalAudioTrack: (track: JitsiLocalTrack | null) => void;
  setLocalScreenShareTrack: (track: JitsiLocalTrack | null) => void;
  setRemoteTrack: (participantId: string, kind: 'video' | 'audio' | 'screenShare', track: JitsiRemoteTrack | null) => void;
  removeParticipantTracks: (participantId: string) => void;
}

// ─── 액션 (안정 참조, trackStore 메서드 직접 바인딩) ──────────────────────────

const trackActions: TrackActions = {
  setLocalVideoTrack: (track) => trackStore.setLocalVideoTrack(track),
  setLocalAudioTrack: (track) => trackStore.setLocalAudioTrack(track),
  setLocalScreenShareTrack: (track) => trackStore.setLocalScreenShareTrack(track),
  setRemoteTrack: (participantId, kind, track) => trackStore.setRemoteTrack(participantId, kind, track),
  removeParticipantTracks: (participantId) => trackStore.removeParticipantTracks(participantId),
};

// ─── Provider (하위 호환 — 이제 단순 passthrough) ────────────────────────────

export const TrackProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return <>{children}</>;
};

// ─── Hooks ────────────────────────────────────────────────────────────────────

/** trackStore의 액션 함수들을 반환 (안정 참조) */
export function useTrackActions(): TrackActions {
  return trackActions;
}

/** 하위 호환: 기존 useTrackContext 대체 */
export function useTrackContext(): TrackActions & { tracks: TrackState } {
  const localVideo = _useLocalVideoTrack();
  const localAudio = _useLocalAudioTrack();
  const localScreenShare = _useLocalScreenShareTrack();
  const remote = _useAllRemoteTracks();
  return {
    tracks: { localVideo, localAudio, localScreenShare, remote },
    ...trackActions,
  };
}

export const useLocalVideoTrack = _useLocalVideoTrack;
export const useLocalAudioTrack = _useLocalAudioTrack;
export const useLocalScreenShareTrack = _useLocalScreenShareTrack;
export const useRemoteTracks = _useRemoteTracks;
export const useAllRemoteTracks = _useAllRemoteTracks;
